import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Avatar,
  IconButton,
  Button,
  Paper,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Collapse
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Assessment as AssessmentIcon,
  Science as ScienceIcon,
  MedicalServices as MedicalServicesIcon,
  SwapHoriz as SwapHorizIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Circle as CircleIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const TriageBoard = ({ facilityId }) => {
  const theme = useTheme();
  const [patients, setPatients] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    critical: false,
    myPatients: false,
    expected: false,
    wrapUp: false
  });
  const [sortBy, setSortBy] = useState('priority');

  // New state for dialogs
  const [assignDialog, setAssignDialog] = useState(false);
  const [treatmentTeamDialog, setTreatmentTeamDialog] = useState(false);
  const [patientStatusDialog, setPatientStatusDialog] = useState(false);
  const [labStatusDialog, setLabStatusDialog] = useState(false);
  const [imagingStatusDialog, setImagingStatusDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);

  // Form states for dialogs
  const [assignTo, setAssignTo] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [labNotes, setLabNotes] = useState('');
  const [imagingNotes, setImagingNotes] = useState('');
  const [transferLocation, setTransferLocation] = useState('');
  const [transferReason, setTransferReason] = useState('');

  // Enhanced mock data with more comprehensive fields
  useEffect(() => {
    const mockPatients = [
      {
        id: '1',
        bed: '35-A',
        name: 'Bernier, Jenny',
        gender: 'F',
        age: '10 y.o',
        complaint: 'Fever and cough',
        chiefComplaint: 'Chief complaint: High fever (103°F) with persistent cough for 2 days',
        status: 'Active',
        triageLevel: 3,
        arrivalTime: '08:45',
        waitTime: '02:15',
        vitals: { bp: '110/70', hr: '95', temp: '103.2°F', spo2: '97%' },
        mdlp: 'Dr. Smith',
        resident: 'R. Johnson [2/7]',
        nurse: 'RN Miller',
        assignedTo: 'Dr. Smith', // Current user for "My Patients" filter
        labStatus: 'Pending CBC, CMP',
        imgStatus: 'CXR Ordered',
        disposition: '',
        bedStatus: 'Assigned',
        isHighPriority: false,
        allergies: ['Penicillin'],
        lastUpdate: '10 min ago',
        tasks: ['Blood draw', 'Vitals q4h'],
        consultants: []
      },
      {
        id: '2',
        bed: 'Waiting',
        name: 'Testing, Natalya',
        gender: 'F',
        age: '40 y.o',
        complaint: 'Chest pain',
        chiefComplaint: 'Chief complaint: Chest pain radiating to left arm, started 1 hour ago',
        status: 'Testing',
        triageLevel: 2,
        arrivalTime: '09:15',
        waitTime: '01:45',
        vitals: { bp: '160/95', hr: '110', temp: '98.6°F', spo2: '96%' },
        mdlp: 'Dr. Wilson [1/2/2]',
        resident: 'R. Davis [2/7]',
        nurse: '',
        assignedTo: 'Dr. Wilson',
        labStatus: 'Troponin Complete, BNP Pending',
        imgStatus: 'EKG Done, Echo Ordered',
        disposition: 'Cardiology',
        bedStatus: 'Waiting',
        isHighPriority: true,
        allergies: ['Contrast dye'],
        lastUpdate: '5 min ago',
        tasks: ['Repeat EKG', 'IV access'],
        consultants: ['Cardiology - Dr. Lee']
      },
      {
        id: '3',
        bed: '6-P',
        name: 'Testone, James',
        gender: 'M',
        age: '22 y.o',
        complaint: 'Nausea, vomiting, abdominal pain',
        chiefComplaint: 'Chief complaint: Severe nausea with vomiting and RLQ abdominal pain for 6 hours',
        status: 'Active',
        triageLevel: 3,
        arrivalTime: '07:30',
        waitTime: '03:30',
        vitals: { bp: '125/80', hr: '88', temp: '100.1°F', spo2: '99%' },
        mdlp: 'Dr. Brown [4/7/8]',
        resident: 'R. Taylor [9/15]',
        nurse: 'RN Garcia',
        assignedTo: 'Dr. Brown',
        labStatus: 'WBC Complete, Lipase Pending',
        imgStatus: 'CT Abd/Pelvis Complete',
        disposition: '',
        bedStatus: 'Assigned',
        isHighPriority: false,
        allergies: [],
        lastUpdate: '15 min ago',
        tasks: ['NPO', 'Pain reassessment'],
        consultants: ['Surgery consult pending']
      },
      {
        id: '4',
        bed: '6-B',
        name: 'Testthree, Beto',
        gender: 'M',
        age: '85 y.o',
        complaint: 'Altered mental status',
        chiefComplaint: 'Chief complaint: Confusion and altered LOC, found down by family',
        status: 'Active',
        triageLevel: 1,
        arrivalTime: '06:15',
        waitTime: '04:45',
        vitals: { bp: '90/60', hr: '65', temp: '96.8°F', spo2: '92%' },
        mdlp: 'Dr. Anderson [6/6/5]',
        resident: 'R. White [0/4]',
        nurse: 'RN Thompson',
        assignedTo: 'Dr. Anderson',
        labStatus: 'CBC, CMP, UA Complete',
        imgStatus: 'CT Head Complete',
        disposition: 'ICU',
        bedStatus: 'Assigned',
        isHighPriority: true,
        allergies: ['Aspirin', 'Sulfa'],
        lastUpdate: '2 min ago',
        tasks: ['Neuro checks q1h', 'Blood cultures'],
        consultants: ['Neurology - Dr. Kim', 'ICU - Dr. Patel']
      },
      {
        id: '5',
        bed: 'Expected',
        name: 'Johnson, Maria',
        gender: 'F',
        age: '67 y.o',
        complaint: 'Chest pain - EMS transport',
        chiefComplaint: 'Chief complaint: Chest pain with SOB, ETA 15 minutes via ambulance',
        status: 'Expected',
        triageLevel: 2,
        arrivalTime: 'ETA 10:30',
        waitTime: 'N/A',
        vitals: { bp: 'N/A', hr: 'N/A', temp: 'N/A', spo2: 'N/A' },
        mdlp: '',
        resident: '',
        nurse: '',
        assignedTo: 'Dr. Smith', // Pre-assigned for expected patient
        labStatus: '',
        imgStatus: '',
        disposition: '',
        bedStatus: 'Expected',
        isHighPriority: true,
        allergies: [],
        lastUpdate: '3 min ago',
        tasks: ['Prepare Room 12', 'Alert cardiology'],
        consultants: [],
        isExpected: true
      },
      {
        id: '6',
        bed: '22-A',
        name: 'Williams, Robert',
        gender: 'M',
        age: '55 y.o',
        complaint: 'Stable post-procedure',
        chiefComplaint: 'Chief complaint: Post-cardiac catheterization, stable for discharge',
        status: 'Wrap-up',
        triageLevel: 4,
        arrivalTime: '05:30',
        waitTime: '05:30',
        vitals: { bp: '130/82', hr: '72', temp: '98.4°F', spo2: '98%' },
        mdlp: 'Dr. Smith',
        resident: '',
        nurse: 'RN Clark',
        assignedTo: 'Dr. Smith',
        labStatus: 'All Complete',
        imgStatus: 'Echo Complete',
        disposition: 'Discharge',
        bedStatus: 'Assigned',
        isHighPriority: false,
        allergies: ['Iodine'],
        lastUpdate: '8 min ago',
        tasks: ['Discharge instructions', 'Follow-up appointment'],
        consultants: [],
        isWrapUp: true
      }
    ];

    setPatients(mockPatients);
    setLoading(false);
  }, [facilityId]);

  const handleRightClick = (event, patient) => {
    event.preventDefault();
    setSelectedPatient(patient);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setSelectedPatient(null);
  };

  const handleMenuAction = (action) => {
    handleContextMenuClose();
    
    switch (action) {
      case 'Assign Me':
        handleAssignMe();
        break;
      case 'Treatment Team':
        setTreatmentTeamDialog(true);
        break;
      case 'Patient Status':
        setNewStatus(selectedPatient?.status || '');
        setPatientStatusDialog(true);
        break;
      case 'Lab Status':
        setLabNotes(selectedPatient?.labStatus || '');
        setLabStatusDialog(true);
        break;
      case 'Imaging Status':
        setImagingNotes(selectedPatient?.imgStatus || '');
        setImagingStatusDialog(true);
        break;
      case 'Transfer':
        setTransferDialog(true);
        break;
      default:
        console.log(`${action} for patient:`, selectedPatient);
    }
  };

  const handleAssignMe = () => {
    if (selectedPatient) {
      setPatients(prev => prev.map(patient => 
        patient.id === selectedPatient.id 
          ? { ...patient, assignedTo: currentUser, mdlp: currentUser }
          : patient
      ));
    }
  };

  const handleAssignToProvider = () => {
    if (selectedPatient && assignTo) {
      setPatients(prev => prev.map(patient => 
        patient.id === selectedPatient.id 
          ? { ...patient, assignedTo: assignTo, mdlp: assignTo }
          : patient
      ));
      setAssignDialog(false);
      setAssignTo('');
    }
  };

  const handleStatusUpdate = () => {
    if (selectedPatient && newStatus) {
      setPatients(prev => prev.map(patient => 
        patient.id === selectedPatient.id 
          ? { ...patient, status: newStatus }
          : patient
      ));
      setPatientStatusDialog(false);
      setNewStatus('');
    }
  };

  const handleLabStatusUpdate = () => {
    if (selectedPatient && labNotes) {
      setPatients(prev => prev.map(patient => 
        patient.id === selectedPatient.id 
          ? { ...patient, labStatus: labNotes }
          : patient
      ));
      setLabStatusDialog(false);
      setLabNotes('');
    }
  };

  const handleImagingStatusUpdate = () => {
    if (selectedPatient && imagingNotes) {
      setPatients(prev => prev.map(patient => 
        patient.id === selectedPatient.id 
          ? { ...patient, imgStatus: imagingNotes }
          : patient
      ));
      setImagingStatusDialog(false);
      setImagingNotes('');
    }
  };

  const handleTransfer = () => {
    if (selectedPatient && transferLocation) {
      setPatients(prev => prev.map(patient => 
        patient.id === selectedPatient.id 
          ? { ...patient, disposition: transferLocation, status: 'Transfer Pending' }
          : patient
      ));
      setTransferDialog(false);
      setTransferLocation('');
      setTransferReason('');
    }
  };

  const getTriageColor = (level) => {
    switch (level) {
      case 1: return '#f44336'; // Red - Critical
      case 2: return '#ff9800'; // Orange - Urgent
      case 3: return '#ffeb3b'; // Yellow - Less urgent
      case 4: return '#4caf50'; // Green - Non-urgent
      case 5: return '#2196f3'; // Blue - Fast track
      default: return '#9e9e9e';
    }
  };



  const getStatusIcon = (status, isHighPriority) => {
    if (isHighPriority) {
      return <WarningIcon sx={{ color: '#f44336', fontSize: '1rem' }} />;
    }
    switch (status) {
      case 'Active': return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1rem' }} />;
      case 'Testing': return <PendingIcon sx={{ color: '#ff9800', fontSize: '1rem' }} />;
      case 'Screening': return <CircleIcon sx={{ color: '#2196f3', fontSize: '1rem' }} />;
      case 'Expected': return <ScheduleIcon sx={{ color: '#ff9800', fontSize: '1rem' }} />;
      case 'Wrap-up': return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1rem' }} />;
      default: return <CircleIcon sx={{ color: '#9e9e9e', fontSize: '1rem' }} />;
    }
  };

  const formatWaitTime = (waitTime) => {
    return waitTime;
  };

  const handleFilterToggle = (filterType) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  // Mock current user - in real app this would come from auth context
  const currentUser = 'Dr. Smith';

  const filteredPatients = patients.filter(patient => {
    // Apply filters based on active filters
    if (activeFilters.critical && !(patient.triageLevel <= 2 || patient.isHighPriority)) {
      return false;
    }
    
    if (activeFilters.myPatients && patient.assignedTo !== currentUser) {
      return false;
    }
    
    if (activeFilters.expected && !patient.isExpected) {
      return false;
    }
    
    if (activeFilters.wrapUp && !patient.isWrapUp) {
      return false;
    }
    
    return true;
  });

  const sortedPatients = [...filteredPatients].sort((a, b) => {
    switch (sortBy) {
      case 'priority':
        return a.triageLevel - b.triageLevel;
      case 'waitTime':
        return b.waitTime.localeCompare(a.waitTime);
      case 'bed':
        return a.bed.localeCompare(b.bed);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Purple/Blue Gradient Header */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                ER Triage Board
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {filteredPatients.length} of {patients.length} patients • {patients.filter(p => p.triageLevel <= 2).length} critical • {patients.filter(p => p.isExpected).length} expected • {patients.filter(p => p.isWrapUp).length} wrap-up
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label="My Patients"
                  size="small"
                  variant={activeFilters.myPatients ? "filled" : "outlined"}
                  color={activeFilters.myPatients ? "primary" : "default"}
                  onClick={() => handleFilterToggle('myPatients')}
                  sx={{ 
                    fontSize: '0.7rem', 
                    height: 24, 
                    cursor: 'pointer',
                    backgroundColor: activeFilters.myPatients ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                />
                <Chip
                  label="Critical"
                  size="small"
                  variant={activeFilters.critical ? "filled" : "outlined"}
                  color={activeFilters.critical ? "error" : "default"}
                  onClick={() => handleFilterToggle('critical')}
                  sx={{ 
                    fontSize: '0.7rem', 
                    height: 24, 
                    cursor: 'pointer',
                    backgroundColor: activeFilters.critical ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                />
                <Chip
                  label="Expected"
                  size="small"
                  variant={activeFilters.expected ? "filled" : "outlined"}
                  color={activeFilters.expected ? "warning" : "default"}
                  onClick={() => handleFilterToggle('expected')}
                  sx={{ 
                    fontSize: '0.7rem', 
                    height: 24, 
                    cursor: 'pointer',
                    backgroundColor: activeFilters.expected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                />
                <Chip
                  label="Wrap-up"
                  size="small"
                  variant={activeFilters.wrapUp ? "filled" : "outlined"}
                  color={activeFilters.wrapUp ? "success" : "default"}
                  onClick={() => handleFilterToggle('wrapUp')}
                  sx={{ 
                    fontSize: '0.7rem', 
                    height: 24, 
                    cursor: 'pointer',
                    backgroundColor: activeFilters.wrapUp ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                />
              </Box>
              
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ 
                    fontSize: '0.8rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiSelect-icon': {
                      color: 'white',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    }
                  }}
                >
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="waitTime">Wait Time</MenuItem>
                  <MenuItem value="bed">Bed</MenuItem>
                </Select>
              </FormControl>

              <IconButton 
                size="small"
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Compact Table */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ height: '100%' }}>
            <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { padding: '4px 8px' } }}>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#f5f5f5', fontWeight: 700, fontSize: '0.8rem' } }}>
                  <TableCell sx={{ width: 40 }}></TableCell>
                  <TableCell sx={{ width: 60 }}>Triage</TableCell>
                  <TableCell sx={{ width: 80 }}>Bed</TableCell>
                  <TableCell sx={{ width: 180 }}>Patient</TableCell>
                  <TableCell sx={{ width: 60 }}>Age</TableCell>
                  <TableCell sx={{ width: 200 }}>Complaint</TableCell>
                  <TableCell sx={{ width: 120 }}>Vitals</TableCell>
                  <TableCell sx={{ width: 100 }}>Wait Time</TableCell>
                  <TableCell sx={{ width: 120 }}>Staff</TableCell>
                  <TableCell sx={{ width: 150 }}>Status</TableCell>
                  <TableCell sx={{ width: 100 }}>Disposition</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPatients.map((patient) => (
                  <React.Fragment key={patient.id}>
                    <TableRow
                      hover
                      onContextMenu={(e) => handleRightClick(e, patient)}
                      onClick={() => setExpandedPatient(expandedPatient === patient.id ? null : patient.id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: patient.isHighPriority ? 'rgba(244, 67, 54, 0.05)' : 'inherit',
                        borderLeft: `4px solid ${getTriageColor(patient.triageLevel)}`,
                        height: 48,
                        '&:hover': {
                          backgroundColor: patient.isHighPriority ? 'rgba(244, 67, 54, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                    >
                      {/* Expand Icon */}
                      <TableCell>
                        <IconButton size="small" sx={{ p: 0.5 }}>
                          {expandedPatient === patient.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>

                      {/* Triage Level */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Badge
                            badgeContent={patient.triageLevel}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: getTriageColor(patient.triageLevel),
                                color: 'white',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                width: 18,
                                height: 18,
                                minWidth: 18
                              }
                            }}
                          >
                            <Box sx={{ width: 16, height: 16 }} />
                          </Badge>
                          {getStatusIcon(patient.status, patient.isHighPriority)}
                        </Box>
                      </TableCell>

                                             {/* Bed */}
                       <TableCell>
                         <Chip 
                           label={
                             patient.bed === 'Waiting' ? 'WAIT' : 
                             patient.bed === 'Expected' ? 'EXP' : 
                             patient.bed
                           }
                           size="small"
                           sx={{
                             backgroundColor: 
                               patient.bed === 'Waiting' ? '#ffeb3b' : 
                               patient.bed === 'Expected' ? '#fff3e0' :
                               patient.status === 'Wrap-up' ? '#e8f5e8' :
                               '#e3f2fd',
                             color: 
                               patient.bed === 'Waiting' ? '#333' : 
                               patient.bed === 'Expected' ? '#f57c00' :
                               patient.status === 'Wrap-up' ? '#2e7d32' :
                               '#1976d2',
                             fontWeight: 'bold',
                             fontSize: '0.7rem',
                             height: 20,
                             minWidth: 50
                           }}
                         />
                       </TableCell>

                      {/* Patient */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: theme.palette.primary.main }}>
                            {patient.name.split(',')[0].charAt(0)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                              {patient.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              ({patient.gender}) • {patient.arrivalTime}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Age */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {patient.age}
                        </Typography>
                      </TableCell>

                      {/* Complaint */}
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 180
                          }}
                        >
                          {patient.complaint}
                        </Typography>
                      </TableCell>

                      {/* Vitals */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', minWidth: 35 }}>
                              BP: <strong>{patient.vitals.bp}</strong>
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', minWidth: 35 }}>
                              HR: <strong>{patient.vitals.hr}</strong>
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontSize: '0.7rem', 
                                minWidth: 35,
                                color: patient.vitals.temp.includes('103') ? '#f44336' : 'inherit' 
                              }}
                            >
                              T: <strong>{patient.vitals.temp}</strong>
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontSize: '0.7rem', 
                                minWidth: 35,
                                color: parseInt(patient.vitals.spo2) < 95 ? '#f44336' : '#4caf50' 
                              }}
                            >
                              O2: <strong>{patient.vitals.spo2}</strong>
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Wait Time */}
                      <TableCell>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#f57c00' }}>
                            {formatWaitTime(patient.waitTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            waiting
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Staff */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {patient.mdlp && (
                            <Chip 
                              label={`MD: ${patient.mdlp.split(' ')[1] || patient.mdlp.slice(0, 6)}`} 
                              size="small" 
                              variant="outlined" 
                              sx={{ fontSize: '0.6rem', height: 16 }} 
                            />
                          )}
                          {patient.nurse && (
                            <Chip 
                              label={patient.nurse.replace('RN ', '')} 
                              size="small" 
                              variant="outlined" 
                              sx={{ fontSize: '0.6rem', height: 16 }} 
                            />
                          )}
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {patient.labStatus && (
                            <Chip 
                              label={patient.labStatus.includes('Complete') ? '✓ Lab' : '⏳ Lab'} 
                              size="small" 
                              sx={{ 
                                fontSize: '0.6rem', 
                                height: 16, 
                                backgroundColor: patient.labStatus.includes('Complete') ? '#e8f5e8' : '#fff3e0',
                                color: patient.labStatus.includes('Complete') ? '#2e7d32' : '#f57c00'
                              }} 
                            />
                          )}
                          {patient.imgStatus && (
                            <Chip 
                              label={patient.imgStatus.includes('Complete') ? '✓ Img' : '⏳ Img'} 
                              size="small" 
                              sx={{ 
                                fontSize: '0.6rem', 
                                height: 16,
                                backgroundColor: patient.imgStatus.includes('Complete') ? '#e8f5e8' : '#fff3e0',
                                color: patient.imgStatus.includes('Complete') ? '#2e7d32' : '#f57c00'
                              }} 
                            />
                          )}
                          {patient.consultants.length > 0 && (
                            <Chip 
                              label={`${patient.consultants.length} Consult`} 
                              size="small" 
                              sx={{ fontSize: '0.6rem', height: 16, backgroundColor: '#e3f2fd', color: '#1976d2' }} 
                            />
                          )}
                          {patient.allergies.length > 0 && (
                            <Chip 
                              label="⚠ Allergy" 
                              size="small" 
                              sx={{ fontSize: '0.6rem', height: 16, backgroundColor: '#ffebee', color: '#d32f2f' }} 
                            />
                          )}
                        </Box>
                      </TableCell>

                      {/* Disposition */}
                      <TableCell>
                        {patient.disposition && (
                          <Chip 
                            label={patient.disposition} 
                            size="small" 
                            sx={{ fontSize: '0.7rem', height: 18, backgroundColor: '#f3e5f5', color: '#7b1fa2' }} 
                          />
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expandable Details Row */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                        <Collapse in={expandedPatient === patient.id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              Detailed Information
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                  Chief Complaint:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 1 }}>
                                  {patient.chiefComplaint}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  Lab Status:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {patient.labStatus || 'None ordered'}
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  Imaging Status:
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {patient.imgStatus || 'None ordered'}
                                </Typography>
                              </Box>
                            </Box>

                            {patient.allergies.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                  Allergies:
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#d32f2f', fontSize: '0.8rem' }}>
                                  {patient.allergies.join(', ')}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 3 }}>
                              {patient.consultants.length > 0 && (
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    Consultants:
                                  </Typography>
                                  {patient.consultants.map((consultant, index) => (
                                    <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                                      • {consultant}
                                    </Typography>
                                  ))}
                                </Box>
                              )}

                              {patient.tasks.length > 0 && (
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    Active Tasks:
                                  </Typography>
                                  {patient.tasks.map((task, index) => (
                                    <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem' }}>
                                      • {task}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Last updated: {patient.lastUpdate}
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        <MenuItem onClick={() => handleMenuAction('Assign Me')}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign Me</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('Treatment Team')}>
          <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Treatment Team</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('Patient Status')}>
          <ListItemIcon><AssessmentIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Patient Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('Lab Status')}>
          <ListItemIcon><ScienceIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Lab Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('Imaging Status')}>
          <ListItemIcon><MedicalServicesIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Imaging Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('Transfer')}>
          <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Transfer</ListItemText>
        </MenuItem>
      </Menu>

      {/* Treatment Team Dialog */}
      <Dialog open={treatmentTeamDialog} onClose={() => setTreatmentTeamDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon />
            Assign Treatment Team - {selectedPatient?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Attending Physician</InputLabel>
              <Select
                value={assignTo}
                onChange={(e) => setAssignTo(e.target.value)}
                label="Attending Physician"
              >
                <MenuItem value="Dr. Smith">Dr. Smith</MenuItem>
                <MenuItem value="Dr. Wilson">Dr. Wilson</MenuItem>
                <MenuItem value="Dr. Brown">Dr. Brown</MenuItem>
                <MenuItem value="Dr. Anderson">Dr. Anderson</MenuItem>
                <MenuItem value="Dr. Taylor">Dr. Taylor</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Assigned Nurse</InputLabel>
              <Select
                label="Assigned Nurse"
              >
                <MenuItem value="RN Miller">RN Miller</MenuItem>
                <MenuItem value="RN Garcia">RN Garcia</MenuItem>
                <MenuItem value="RN Thompson">RN Thompson</MenuItem>
                <MenuItem value="RN Johnson">RN Johnson</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Resident</InputLabel>
              <Select
                label="Resident"
              >
                <MenuItem value="R. Johnson">R. Johnson</MenuItem>
                <MenuItem value="R. Davis">R. Davis</MenuItem>
                <MenuItem value="R. Taylor">R. Taylor</MenuItem>
                <MenuItem value="R. White">R. White</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTreatmentTeamDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignToProvider} variant="contained">Assign Team</Button>
        </DialogActions>
      </Dialog>

      {/* Patient Status Dialog */}
      <Dialog open={patientStatusDialog} onClose={() => setPatientStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon />
            Update Patient Status - {selectedPatient?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Testing">Testing</MenuItem>
                <MenuItem value="Screening">Screening</MenuItem>
                <MenuItem value="Expected">Expected</MenuItem>
                <MenuItem value="Wrap-up">Wrap-up</MenuItem>
                <MenuItem value="Discharged">Discharged</MenuItem>
                <MenuItem value="Admitted">Admitted</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatientStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">Update Status</Button>
        </DialogActions>
      </Dialog>

      {/* Lab Status Dialog */}
      <Dialog open={labStatusDialog} onClose={() => setLabStatusDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon />
            Update Lab Status - {selectedPatient?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Lab Status"
              value={labNotes}
              onChange={(e) => setLabNotes(e.target.value)}
              placeholder="e.g., CBC Complete, CMP Pending, Troponin Normal"
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="CBC Complete" onClick={() => setLabNotes(prev => prev + (prev ? ', ' : '') + 'CBC Complete')} />
              <Chip label="CMP Pending" onClick={() => setLabNotes(prev => prev + (prev ? ', ' : '') + 'CMP Pending')} />
              <Chip label="Troponin Complete" onClick={() => setLabNotes(prev => prev + (prev ? ', ' : '') + 'Troponin Complete')} />
              <Chip label="BNP Pending" onClick={() => setLabNotes(prev => prev + (prev ? ', ' : '') + 'BNP Pending')} />
              <Chip label="UA Complete" onClick={() => setLabNotes(prev => prev + (prev ? ', ' : '') + 'UA Complete')} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleLabStatusUpdate} variant="contained">Update Lab Status</Button>
        </DialogActions>
      </Dialog>

      {/* Imaging Status Dialog */}
      <Dialog open={imagingStatusDialog} onClose={() => setImagingStatusDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MedicalServicesIcon />
            Update Imaging Status - {selectedPatient?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Imaging Status"
              value={imagingNotes}
              onChange={(e) => setImagingNotes(e.target.value)}
              placeholder="e.g., CXR Complete, CT Abd/Pelvis Ordered, Echo Done"
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="CXR Complete" onClick={() => setImagingNotes(prev => prev + (prev ? ', ' : '') + 'CXR Complete')} />
              <Chip label="CT Head Complete" onClick={() => setImagingNotes(prev => prev + (prev ? ', ' : '') + 'CT Head Complete')} />
              <Chip label="CT Abd/Pelvis Ordered" onClick={() => setImagingNotes(prev => prev + (prev ? ', ' : '') + 'CT Abd/Pelvis Ordered')} />
              <Chip label="Echo Ordered" onClick={() => setImagingNotes(prev => prev + (prev ? ', ' : '') + 'Echo Ordered')} />
              <Chip label="EKG Done" onClick={() => setImagingNotes(prev => prev + (prev ? ', ' : '') + 'EKG Done')} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagingStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleImagingStatusUpdate} variant="contained">Update Imaging Status</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onClose={() => setTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon />
            Transfer Patient - {selectedPatient?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Transfer To</InputLabel>
              <Select
                value={transferLocation}
                onChange={(e) => setTransferLocation(e.target.value)}
                label="Transfer To"
              >
                <MenuItem value="ICU">ICU</MenuItem>
                <MenuItem value="Cardiology">Cardiology</MenuItem>
                <MenuItem value="Surgery">Surgery</MenuItem>
                <MenuItem value="Internal Medicine">Internal Medicine</MenuItem>
                <MenuItem value="Neurology">Neurology</MenuItem>
                <MenuItem value="Pediatrics">Pediatrics</MenuItem>
                <MenuItem value="Another Hospital">Another Hospital</MenuItem>
                <MenuItem value="Home">Discharge to Home</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Transfer Reason"
              value={transferReason}
              onChange={(e) => setTransferReason(e.target.value)}
              placeholder="Reason for transfer or discharge"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(false)}>Cancel</Button>
          <Button onClick={handleTransfer} variant="contained">Transfer Patient</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TriageBoard; 